
import React from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Users, Check, X } from 'lucide-react';
import { useParentAccessRequests, ParentAccessRequest } from '@/hooks/useParentAccessRequests';
import { toast } from 'sonner';

interface ParentAccessRequestAlertProps {
  request: ParentAccessRequest;
  onResponse?: () => void;
}

const ParentAccessRequestAlert: React.FC<ParentAccessRequestAlertProps> = ({ 
  request, 
  onResponse 
}) => {
  const { respondToRequest } = useParentAccessRequests();

  const handleResponse = async (response: 'approved' | 'rejected') => {
    const success = await respondToRequest(request.id, response);
    
    if (success) {
      const action = response === 'approved' ? 'approved' : 'rejected';
      toast.success(`Parent access request ${action}`);
      onResponse?.();
    } else {
      toast.error('Failed to respond to request');
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    return `${Math.floor(diffInHours / 24)}d ago`;
  };

  return (
    <Alert variant="info" className="mb-4">
      <Users className="h-4 w-4" />
      <AlertTitle className="flex items-center justify-between">
        Parent Access Request
        <Badge variant="secondary">{formatTimeAgo(request.requested_at)}</Badge>
      </AlertTitle>
      <AlertDescription>
        <div className="space-y-3">
          <p>
            <strong>{request.parent?.name || 'A parent'}</strong> has requested access to your athlete profile.
          </p>
          {request.parent?.email && (
            <p className="text-sm text-muted-foreground">
              Email: {request.parent.email}
            </p>
          )}
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => handleResponse('approved')}
              className="bg-green-600 hover:bg-green-700"
            >
              <Check className="h-4 w-4 mr-1" />
              Approve
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleResponse('rejected')}
              className="border-red-200 text-red-600 hover:bg-red-50"
            >
              <X className="h-4 w-4 mr-1" />
              Decline
            </Button>
          </div>
        </div>
      </AlertDescription>
    </Alert>
  );
};

export default ParentAccessRequestAlert;
